/**
 * Resize an image blob to fit within maxWidth, exported as JPEG.
 * Only downscales — returns original blob if already smaller.
 */
async function resizeImageBlob(blob, maxWidth, quality) {
  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(blob);

    img.onload = () => {
      URL.revokeObjectURL(url);

      if (img.width <= maxWidth) {
        resolve(blob);
        return;
      }

      const scale = maxWidth / img.width;
      const width = maxWidth;
      const height = Math.round(img.height * scale);

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);

      canvas.toBlob(
        (resized) => resolve(resized),
        'image/jpeg',
        quality
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(blob);
    };

    img.src = url;
  });
}

/**
 * Convert a Blob to a base64 data string via FileReader.
 */
function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

/**
 * Fetch a single image URL and return as base64.
 *
 * @param {string} url — image URL to fetch
 * @param {number} timeoutMs — abort after this many ms (default 5000)
 * @returns {{ base64: string, mimeType: string } | null}
 */
export async function fetchImageAsBase64(url, timeoutMs = 5000) {
  try {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);

    const response = await fetch(url, { signal: controller.signal });
    clearTimeout(timer);

    if (!response.ok) {
      console.warn(`[imageExportUtils] Failed to fetch ${url}: ${response.status}`);
      return null;
    }

    const blob = await response.blob();
    const mimeType = blob.type || 'image/jpeg';
    const dataUrl = await blobToBase64(blob);
    // dataUrl is "data:<mime>;base64,<data>" — extract just the base64 part
    const base64 = dataUrl.split(',')[1];

    return { base64, mimeType };
  } catch (err) {
    console.warn(`[imageExportUtils] Error fetching ${url}:`, err.message || err);
    return null;
  }
}

/**
 * Fill in missing day hero images by searching Pexels for the city name.
 * Mutates exportData.days in place — sets heroImageUrl on days that lack one.
 * Skips transit days.
 */
async function fillMissingDayHeroImages(exportData) {
  // Lazy import to avoid circular deps and keep this module independently loadable
  const { searchPhoto } = await import('./pexels');

  const searches = exportData.days
    .filter(d => !d.heroImageUrl && d.city && !d.isTransitDay)
    .map(async (day) => {
      try {
        const url = await searchPhoto(`${day.city} cityscape travel`);
        if (url) {
          day.heroImageUrl = url;
        }
      } catch (e) {
        console.warn(`[imageExportUtils] Pexels fallback failed for ${day.city}:`, e);
      }
    });

  await Promise.allSettled(searches);
}

/**
 * Fetch and optionally resize all images referenced in exportData.
 *
 * @param {Object} exportData — output from prepareExportData()
 * @param {Object} options
 * @returns {Map<string, { base64: string, mimeType: string }>}
 */
export async function fetchAllExportImages(exportData, options = {}) {
  const {
    heroQuality = 0.7,
    thumbnailQuality = 0.5,
    thumbnailMaxWidth = 200,
    heroMaxWidth = 800,
    timeoutMs = 5000,
  } = options;

  // Fill in missing day hero images from Pexels before collecting URLs
  await fillMissingDayHeroImages(exportData);

  // Collect all image URLs with their target sizing
  const imageJobs = [];

  if (exportData.trip.heroImageUrl) {
    imageJobs.push({
      url: exportData.trip.heroImageUrl,
      maxWidth: heroMaxWidth,
      quality: heroQuality,
    });
  }

  for (const day of exportData.days) {
    // Day hero images at hero quality
    if (day.heroImageUrl) {
      imageJobs.push({
        url: day.heroImageUrl,
        maxWidth: heroMaxWidth,
        quality: heroQuality,
      });
    }

    for (const activity of day.activities) {
      if (activity.imageUrl) {
        imageJobs.push({
          url: activity.imageUrl,
          maxWidth: thumbnailMaxWidth,
          quality: thumbnailQuality,
        });
      }
    }
  }

  // Deduplicate by URL
  const uniqueJobs = new Map();
  for (const job of imageJobs) {
    if (!uniqueJobs.has(job.url)) {
      uniqueJobs.set(job.url, job);
    }
  }

  // Fetch all in parallel
  const entries = [...uniqueJobs.values()];
  const results = await Promise.allSettled(
    entries.map(async (job) => {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), timeoutMs);

      const response = await fetch(job.url, { signal: controller.signal });
      clearTimeout(timer);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      let blob = await response.blob();
      blob = await resizeImageBlob(blob, job.maxWidth, job.quality);

      const mimeType = blob.type || 'image/jpeg';
      const dataUrl = await blobToBase64(blob);
      const base64 = dataUrl.split(',')[1];

      return { url: job.url, base64, mimeType };
    })
  );

  const imageMap = new Map();
  let successCount = 0;

  for (const result of results) {
    if (result.status === 'fulfilled') {
      const { url, base64, mimeType } = result.value;
      imageMap.set(url, { base64, mimeType });
      successCount++;
    } else {
      console.warn('[imageExportUtils] Image fetch failed:', result.reason?.message || result.reason);
    }
  }

  console.log(`[imageExportUtils] Fetched ${successCount}/${entries.length} images successfully`);

  return imageMap;
}
